/**
 * atomberg-support-otp.js
 *
 * OTP send/verify widget for the Support page forms. Binds to every
 * [data-support-otp] block (a phone input + "Verify" button) and drives the
 * single shared atomberg-sheet modal (id="atomberg-support-otp-sheet",
 * rendered once by atomberg-support-otp-modal.liquid, variant: center).
 *
 * On successful verify: disables the phone input, shows a checkmark + Change
 * link, and dispatches a bubbling `atomberg:support-otp-changed` event with
 * {verified, mobile} so the parent form can gate its own submit button.
 * Editing the phone number again (via the Change link) clears verified state.
 *
 * Endpoints (see docs/components/atomberg-support/README.md):
 *   POST {api_base_url}/api/support/otp/send   {mobile_number, purpose}
 *   POST {api_base_url}/api/support/otp/verify {mobile_number, otp, purpose}
 *
 * OTP fails CLOSED by design — an unverified phone always blocks submission.
 * This is the identity gate for the form, not optional business logic (unlike
 * the cart's Clickpost/serviceability checks, which have fail-open policies).
 *
 * Also owns a small generic behaviour unrelated to OTP itself: any
 * [data-support-digits-only] input (phone, the OTP code field, Track
 * Complaint's mobile field, Register Warranty's standalone pincode field)
 * strips non-digit characters as the user types. type="tel"/type="text" do
 * NOT restrict input to digits the way type="number" would — inputmode
 * only hints at a numeric virtual keyboard on mobile, it doesn't validate
 * or filter on desktop — so without this, letters can be typed into fields
 * that are numeric-only by contract, only caught later at submit-time
 * validation. This file loads on both the Support and Warranty pages
 * (every page rendering the OTP modal loads it), making it one shared home
 * for this instead of duplicating the same filter in three separate files.
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergSupportOtpInit) return;
  w._atombergSupportOtpInit = true;

  var MODAL_ID = 'atomberg-support-otp-sheet';
  var RESEND_SECONDS = 10;

  /** @type {HTMLElement|null} the [data-support-otp] block the open modal belongs to */
  var activeBlock = null;
  /** @type {number|null} */
  var resendTimer = null;

  /** @returns {string} */
  function getApiBaseUrl() {
    var root = document.querySelector('[data-support-api-base-url]');
    return (root && root.getAttribute('data-support-api-base-url')) || '';
  }

  /**
   * @param {string} apiBaseUrl
   * @param {string} mobile
   * @param {string} purpose
   * @returns {Promise<{status: boolean, message: string}>}
   */
  function sendOtp(apiBaseUrl, mobile, purpose) {
    return fetch(apiBaseUrl + '/api/support/otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile_number: mobile, purpose: purpose }),
    })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { status: false, message: 'Could not send OTP. Please try again.' };
      });
  }

  /**
   * @param {string} apiBaseUrl
   * @param {string} mobile
   * @param {string} otp
   * @param {string} purpose
   * @returns {Promise<{status: boolean, message: string, token?: string}>}
   */
  function verifyOtp(apiBaseUrl, mobile, otp, purpose) {
    return fetch(apiBaseUrl + '/api/support/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mobile_number: mobile, otp: otp, purpose: purpose }),
    })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { status: false, message: 'Could not verify OTP. Please try again.' };
      });
  }

  /** @param {HTMLElement} block @returns {HTMLInputElement|null} */
  function getPhoneInput(block) {
    return /** @type {HTMLInputElement|null} */ (block.querySelector('[data-support-otp-phone]'));
  }

  /** @param {HTMLElement} block @returns {string} */
  function getPurpose(block) {
    return block.getAttribute('data-support-otp-purpose') || 'service_request';
  }

  /**
   * @param {HTMLElement} block
   * @param {boolean} verified
   */
  function setVerifiedState(block, verified) {
    var phoneInput = getPhoneInput(block);
    var verifyBtn = /** @type {HTMLButtonElement|null} */ (
      block.querySelector('[data-support-otp-verify]')
    );
    var statusEl = /** @type {HTMLElement|null} */ (
      block.querySelector('[data-support-otp-status]')
    );
    var onBehalfWrap = /** @type {HTMLElement|null} */ (
      block.querySelector('[data-support-otp-on-behalf]')
    );
    block.setAttribute('data-support-otp-verified', verified ? 'true' : 'false');
    // The one-time verify token authorizes the PII prefill lookup — clear it
    // the moment verified state is lost (Change link).
    if (!verified) block.removeAttribute('data-support-otp-token');
    if (phoneInput) phoneInput.disabled = verified;
    if (verifyBtn) verifyBtn.hidden = verified;
    if (statusEl) statusEl.hidden = !verified;
    if (onBehalfWrap) {
      onBehalfWrap.hidden = !verified;
      // Losing verification (Change) must not let a stale on-behalf number
      // (or a stale checked box) silently ride along under a DIFFERENT phone
      // number's next verify.
      if (!verified) {
        var onBehalfToggle = /** @type {HTMLInputElement|null} */ (
          onBehalfWrap.querySelector('[data-support-otp-on-behalf-toggle]')
        );
        var onBehalfFieldWrap = /** @type {HTMLElement|null} */ (
          onBehalfWrap.querySelector('[data-support-otp-on-behalf-field]')
        );
        var onBehalfInput = /** @type {HTMLInputElement|null} */ (
          onBehalfWrap.querySelector('input[name="on_behalf_mobile_number"]')
        );
        if (onBehalfToggle) onBehalfToggle.checked = false;
        if (onBehalfFieldWrap) onBehalfFieldWrap.hidden = true;
        if (onBehalfInput) onBehalfInput.value = '';
      }
    }
    block.dispatchEvent(
      new CustomEvent('atomberg:support-otp-changed', {
        detail: { verified: verified, mobile: phoneInput ? phoneInput.value.trim() : '' },
        bubbles: true,
      })
    );
  }

  /**
   * @param {HTMLElement} root
   * @param {string} selector
   * @param {string|undefined} value
   */
  function fillIfEmpty(root, selector, value) {
    if (!value) return;
    var el = /** @type {HTMLInputElement|HTMLSelectElement|null} */ (root.querySelector(selector));
    if (!el || el.value) return;
    el.value = value;
  }

  /**
   * Reference parity: a verified number is a known customer — the reference
   * fetches their details right after OTP verify and prefills the form.
   * Only empty fields are filled (typed values win); failures are silent
   * because manual entry is always available.
   * @param {HTMLElement} block
   */
  function prefillFromSalesforce(block) {
    var formRoot = /** @type {HTMLElement|null} */ (block.closest('[data-support-form-root]'));
    var phoneInput = getPhoneInput(block);
    if (!formRoot || !phoneInput) return;
    var root = formRoot;
    var mobile = phoneInput.value.trim();
    var apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl || !mobile) return;
    // purpose + the one-time verify token let the backend authorize this PII
    // read: purpose locates the OTP marker, the token proves THIS session
    // performed the verification — a bare phone number from the network tab
    // is useless without it (enumeration/replay protection).
    var token = block.getAttribute('data-support-otp-token') || '';
    fetch(
      apiBaseUrl +
        '/api/support/address?mobile_number=' +
        encodeURIComponent(mobile) +
        '&purpose=' +
        encodeURIComponent(getPurpose(block)) +
        (token ? '&token=' + encodeURIComponent(token) : ''),
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (res) {
        var data =
          /** @type {{status?: boolean, name?: string, street?: string, city?: string, state?: string, pincode?: string}|null} */ (
            res
          );
        if (!data || !data.status) return;
        fillIfEmpty(root, '[name="name"]', data.name);
        fillIfEmpty(root, '[name="address1"]', data.street);
        fillIfEmpty(root, '[name="city"]', data.city);
        fillIfEmpty(root, '[name="state"]', data.state);
        var pincodeInput = /** @type {HTMLInputElement|null} */ (
          root.querySelector('[name="pincode"]')
        );
        if (pincodeInput && !pincodeInput.value && data.pincode) {
          pincodeInput.value = data.pincode;
          // Re-fires the pincode handlers so installation serviceability and
          // city/state lookup react to the prefilled value.
          pincodeInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      })
      .catch(function () {
        // Prefill is a convenience only.
      });
  }

  /**
   * @param {HTMLElement} modal
   * @param {string} message
   * @param {'error'|'success'|''} type
   */
  function setModalMessage(modal, message, type) {
    var el = /** @type {HTMLElement|null} */ (modal.querySelector('[data-support-otp-message]'));
    if (!el) return;
    el.textContent = message;
    el.className =
      'atomberg-support-otp__message' + (type ? ' atomberg-support-otp__message--' + type : '');
    el.hidden = !message;
  }

  /** @param {HTMLElement} modal */
  function startResendTimer(modal) {
    var resendBtn = /** @type {HTMLButtonElement|null} */ (
      modal.querySelector('[data-support-otp-resend]')
    );
    if (!resendBtn) return;
    var btn = /** @type {HTMLButtonElement} */ (resendBtn);
    var remaining = RESEND_SECONDS;
    btn.disabled = true;
    if (resendTimer) window.clearInterval(resendTimer);
    function tick() {
      btn.textContent = 'Resend in ' + remaining + 's';
      remaining -= 1;
      if (remaining < 0) {
        if (resendTimer) window.clearInterval(resendTimer);
        resendTimer = null;
        btn.disabled = false;
        btn.textContent = 'Resend OTP';
      }
    }
    tick();
    resendTimer = window.setInterval(tick, 1000);
  }

  /** @param {HTMLElement} block */
  function openOtpModal(block) {
    var modalEl = document.getElementById(MODAL_ID);
    var phoneInput = getPhoneInput(block);
    if (!modalEl || !phoneInput) return;
    var modal = /** @type {HTMLElement} */ (modalEl);

    var mobile = phoneInput.value.trim();
    var errEl = /** @type {HTMLElement|null} */ (block.querySelector('[data-support-otp-error]'));
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      if (errEl) {
        errEl.textContent = 'Invalid Mobile number';
        errEl.hidden = false;
      }
      return;
    }
    if (errEl) errEl.hidden = true;

    activeBlock = block;
    var mobileDisplay = modal.querySelector('[data-support-otp-mobile-display]');
    if (mobileDisplay) mobileDisplay.textContent = mobile;
    var otpInput = /** @type {HTMLInputElement|null} */ (
      modal.querySelector('[data-support-otp-input]')
    );
    if (otpInput) otpInput.value = '';
    setModalMessage(modal, '', '');

    sendOtp(getApiBaseUrl(), mobile, getPurpose(block)).then(function (res) {
      if (!res.status) {
        setModalMessage(modal, res.message || 'Could not send OTP. Please try again.', 'error');
        return;
      }
      startResendTimer(modal);
    });

    w.AtombergSheet.open(MODAL_ID);
    if (otpInput) otpInput.focus();
  }

  document.addEventListener('input', function (e) {
    var target = /** @type {HTMLInputElement} */ (e.target);
    if (!target || !target.matches || !target.matches('[data-support-digits-only]')) return;
    var maxLength = target.getAttribute('maxlength');
    var digits = target.value.replace(/\D/g, '');
    target.value = maxLength ? digits.slice(0, parseInt(maxLength, 10)) : digits;
  });

  document.addEventListener('change', function (e) {
    var target = /** @type {HTMLInputElement} */ (e.target);
    if (!target || !target.matches || !target.matches('[data-support-otp-on-behalf-toggle]')) {
      return;
    }
    var wrap = /** @type {HTMLElement|null} */ (target.closest('[data-support-otp-on-behalf]'));
    var fieldWrap = /** @type {HTMLElement|null} */ (
      wrap ? wrap.querySelector('[data-support-otp-on-behalf-field]') : null
    );
    if (!fieldWrap) return;
    fieldWrap.hidden = !target.checked;
    // Unchecking must clear the value, not just hide it — collectTopLevelFields
    // in atomberg-support-forms.js reads every [name] input regardless of
    // visibility, so a stale value would still submit despite the box being
    // unticked.
    if (!target.checked) {
      var onBehalfInput = /** @type {HTMLInputElement|null} */ (
        fieldWrap.querySelector('input[name="on_behalf_mobile_number"]')
      );
      if (onBehalfInput) onBehalfInput.value = '';
    }
  });

  document.addEventListener('click', function (e) {
    var target = /** @type {HTMLElement} */ (e.target);
    if (!target || !target.closest) return;

    var changeTrigger = target.closest('[data-support-otp-change]');
    if (changeTrigger) {
      var blockToChange = /** @type {HTMLElement|null} */ (
        changeTrigger.closest('[data-support-otp]')
      );
      if (blockToChange) setVerifiedState(blockToChange, false);
      return;
    }

    // The modal only ever opens before verification succeeds (the trigger
    // button that opens it is hidden once verified), so the phone input is
    // never disabled at this point — bailing out just needs to close the
    // modal and hand focus back to it for editing, no state to unwind.
    var modalChangeTrigger = target.closest('[data-support-otp-modal-change]');
    if (modalChangeTrigger) {
      var phoneToEdit = activeBlock ? getPhoneInput(activeBlock) : null;
      w.AtombergSheet.close(MODAL_ID);
      if (phoneToEdit) {
        phoneToEdit.focus();
        phoneToEdit.select();
      }
      return;
    }

    var verifyTrigger = target.closest('[data-support-otp-verify]');
    if (verifyTrigger) {
      var block = /** @type {HTMLElement|null} */ (verifyTrigger.closest('[data-support-otp]'));
      if (block) openOtpModal(block);
      return;
    }

    var resendTrigger = /** @type {HTMLButtonElement|null} */ (
      target.closest('[data-support-otp-resend]')
    );
    if (resendTrigger && !resendTrigger.disabled && activeBlock) {
      var resendModalEl = document.getElementById(MODAL_ID);
      var resendPhoneInput = getPhoneInput(activeBlock);
      var resendBlock = activeBlock;
      if (resendModalEl && resendPhoneInput) {
        var resendModal = /** @type {HTMLElement} */ (resendModalEl);
        sendOtp(getApiBaseUrl(), resendPhoneInput.value.trim(), getPurpose(resendBlock)).then(
          function (res) {
            if (!res.status) {
              setModalMessage(resendModal, res.message || 'Could not resend OTP.', 'error');
              return;
            }
            setModalMessage(resendModal, 'OTP resent.', 'success');
            startResendTimer(resendModal);
          }
        );
      }
      return;
    }

    var submitTrigger = target.closest('[data-support-otp-submit]');
    if (submitTrigger) {
      var modalEl = document.getElementById(MODAL_ID);
      if (!modalEl || !activeBlock) return;
      var modal = /** @type {HTMLElement} */ (modalEl);
      var otpInputEl = /** @type {HTMLInputElement|null} */ (
        modal.querySelector('[data-support-otp-input]')
      );
      var otpValue = otpInputEl ? otpInputEl.value.trim() : '';
      if (!/^\d{4,6}$/.test(otpValue)) {
        setModalMessage(modal, 'Enter the OTP you received.', 'error');
        return;
      }
      var phoneInputEl = getPhoneInput(activeBlock);
      var mobileVal = phoneInputEl ? phoneInputEl.value.trim() : '';
      var btn = /** @type {HTMLButtonElement} */ (submitTrigger);
      var purpose = getPurpose(activeBlock);
      var blockRef = activeBlock;
      btn.disabled = true;
      btn.textContent = 'Verifying…';
      verifyOtp(getApiBaseUrl(), mobileVal, otpValue, purpose).then(function (res) {
        btn.disabled = false;
        btn.textContent = 'Verify';
        if (!res.status) {
          setModalMessage(modal, res.message || 'Incorrect OTP. Please try again.', 'error');
          return;
        }
        if (res.token) blockRef.setAttribute('data-support-otp-token', res.token);
        setVerifiedState(blockRef, true);
        prefillFromSalesforce(blockRef);
        w.AtombergSheet.close(MODAL_ID);
      });
    }
  });

  // A disabled input can't be typed into — Change link is the only way back
  // to unverified once verified (handled above), so no separate input
  // listener is needed here.
})();
